<template>
  <div
    id="header-profile"
    class="header-actions"
    v-if="orgDetails"
  >
    <!-- Notification Bell -->
    <div class="notification-wrapper">
      <button class="notification-btn" @click="openNotification">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16ZM16 17H8V11C8 8.52 9.51 6.5 12 6.5C14.49 6.5 16 8.52 16 11V17Z" fill="#475569"/>
        </svg>
        <span v-if="notificationCount" class="notification-badge">
          {{ notificationCount <= 20 ? notificationCount : "20+" }}
        </span>
      </button>
    </div>
    <NotificationModal
      v-if="showNotification"
      title="Notifications"
      :showBackButton="false"
      :bgChange="true"
      @close="showNotification = !showNotification"
    >
      <template v-slot:content>
        <div v-if="showNotification" ref="notification" class="notification-panel">
          <!-- Header Actions -->
          <div class="notification-panel__header">
            <div class="notification-panel__tabs">
              <button 
                class="notification-tab" 
                :class="{ 'notification-tab--active': activeTab === 'all' }"
                @click="activeTab = 'all'"
              >
                All
              </button>
              <button 
                class="notification-tab"
                :class="{ 'notification-tab--active': activeTab === 'unread' }"
                @click="activeTab = 'unread'"
              >
                Unread
                <span v-if="notificationCount" class="notification-tab__badge">{{ notificationCount }}</span>
              </button>
            </div>
            <button 
              v-if="Object.keys(notifications).length > 0 && notificationCount > 0"
              class="mark-all-btn"
              :class="{ 'mark-all-btn--loading': markingAllRead }"
              :disabled="markingAllRead"
              @click="markAllAsRead"
            >
              <span v-if="markingAllRead" class="mark-all-btn__loader"></span>
              {{ markingAllRead ? 'Marking...' : 'Mark all as read' }}
            </button>
          </div>

          <!-- Notification List -->
          <div v-if="!loading" class="notification-panel__content">
            <div v-if="filteredNotifications && Object.keys(filteredNotifications).length > 0">
              <div
                v-for="(group, dateKey) in filteredNotifications"
                :key="'notification-group-' + dateKey"
                class="notification-group"
              >
                <div class="notification-group__date">
                  <span>{{ formatToStringDate(group[0].createdAt) }}</span>
                </div>
                
                <div
                  v-for="(notification, idx) in group"
                  :key="'notification-item-' + idx"
                  class="notification-item"
                  :class="{ 'notification-item--unread': !notification.read }"
                  @click="handleNotificationClick(notification)"
                >
                  <div class="notification-item__indicator" v-if="!notification.read"></div>
                  
                  <div class="notification-item__icon">
                    <img
                      v-if="notification.raw?.owner_image || notification.raw?.token_image"
                      :src="notification.raw?.owner_image || notification.raw?.token_image"
                      alt=""
                    />
                    <div v-else class="notification-item__icon-default">
                      <svg v-if="notification.type === 'Buy' || notification.type === 'Sell'" width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M3.89701 17.86L7.80701 13.95L10.636 16.778L15.207 12.208L17 14V9H12L13.793 10.793L10.636 13.95L7.80801 11.121L2.86201 16.067C2.29194 14.787 1.99821 13.4012 2.00001 12C2.00001 6.477 6.47701 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C10.418 22.001 8.85839 21.6262 7.44962 20.9065C6.04084 20.1867 4.82321 19.1425 3.89701 17.86Z" fill="currentColor"/>
                      </svg>
                      <svg v-else-if="notification.type === 'Esop Invitation' || notification.type === 'Invitation'" width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
                      </svg>
                      <svg v-else-if="notification.type === 'Gift'" width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M20 6H17.82C17.93 5.69 18 5.35 18 5C18 3.34 16.66 2 15 2C13.95 2 13.04 2.54 12.5 3.35L12 4.02L11.5 3.34C10.96 2.54 10.05 2 9 2C7.34 2 6 3.34 6 5C6 5.35 6.07 5.69 6.18 6H4C2.89 6 2.01 6.89 2.01 8L2 19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM15 4C15.55 4 16 4.45 16 5C16 5.55 15.55 6 15 6C14.45 6 14 5.55 14 5C14 4.45 14.45 4 15 4ZM9 4C9.55 4 10 4.45 10 5C10 5.55 9.55 6 9 6C8.45 6 8 5.55 8 5C8 4.45 8.45 4 9 4ZM20 19H4V17H20V19ZM20 14H4V8H9.08L7 10.83L8.62 12L11 8.76L12 7.4L13 8.76L15.38 12L17 10.83L14.92 8H20V14Z" fill="currentColor"/>
                      </svg>
                      <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" fill="currentColor"/>
                      </svg>
                    </div>
                  </div>
                  
                  <div class="notification-item__content">
                    <div class="notification-item__header">
                      <span class="notification-item__type">{{ removeUnderscore(notification.type) }}</span>
                      <span class="notification-item__time">{{ formatISOTime(notification.createdAt) }}</span>
                    </div>
                    <p class="notification-item__message">{{ notification.message }}</p>
                    
                    <!-- Transaction Details -->
                    <div 
                      v-if="notification.type === 'Sell' || notification.type === 'Buy'"
                      class="notification-item__details"
                    >
                      <div class="notification-item__detail">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                          <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                        <span>{{ notification.raw.amount.toLocaleString() }} tokens</span>
                      </div>
                      <div class="notification-item__amount">
                        {{ formatAmountToDollar(notification.raw.price * notification.raw.amount, 0, "USD") }}
                      </div>
                    </div>
                    
                    <!-- Invitation Actions -->
                    <div 
                      v-if="(notification.type === 'Esop Invitation' || notification.type === 'Invitation') && notification.raw.status === 'Pending'"
                      class="notification-item__actions"
                    >
                      <button class="notification-action notification-action--primary">Review Invitation</button>
                    </div>
                    <div 
                      v-else-if="notification.type === 'Esop Invitation' || notification.type === 'Invitation'"
                      class="notification-item__status"
                      :class="{
                        'notification-item__status--accepted': notification.raw.status === 'Accepted',
                        'notification-item__status--terminated': notification.raw.status === 'Terminated'
                      }"
                    >
                      {{ notification.raw.status }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Load More -->
            <div ref="notificationBottom" class="notification-panel__footer">
              <div v-if="loadingMore" class="notification-loader">
                <SmallLoader />
              </div>
              <p v-else-if="Object.keys(filteredNotifications || {}).length > 0" class="notification-end">
                You're all caught up!
              </p>
            </div>
            
            <!-- Empty State -->
            <div v-if="Object.keys(filteredNotifications || {}).length === 0" class="notification-empty">
              <div class="notification-empty__icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" stroke="#CBD5E1" stroke-width="1.5" fill="none"/>
                </svg>
              </div>
              <h3 class="notification-empty__title">
                {{ activeTab === 'unread' ? 'No unread notifications' : 'No notifications yet' }}
              </h3>
              <p class="notification-empty__text">
                {{ activeTab === 'unread' ? "You're all caught up!" : 'When you get notifications, they will show up here' }}
              </p>
            </div>
          </div>
          
          <!-- Loading State -->
          <div v-if="loading" class="notification-panel__loading">
            <SmallLoader />
          </div>
        </div>
      </template>
    </NotificationModal>

    <Popper offsetDistance="-10px">
      <button class="profile-btn">
        <div class="profile-avatar">
          <img
            v-if="orgDetails.image"
            :src="orgDetails.image"
            alt="profile"
            loading="lazy"
          />
          <img
            v-else
            src="https://res.cloudinary.com/djalafcj9/image/upload/v1634012152/getequityV2/header-profile-img_kjtkcq.webp"
            alt="profile"
            loading="lazy"
          />
        </div>
        <div v-if="!teamMember" class="profile-info">
          <span class="profile-name">
            {{ truncate(orgDetails?.name, 12) }}
          </span>
          <span class="profile-role">Admin</span>
        </div>
        <div v-else class="profile-info">
          <span class="profile-name">
            {{ truncate(`${teamMember.fname} ${teamMember.lname}`, 12) }}
          </span>
          <span class="profile-role">
            {{ teamMember.groups?.[0]?.name }}
          </span>
        </div>
        <svg class="dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 8L2 4H10L6 8Z" fill="#94A3B8"/>
        </svg>
      </button>
      <template #content="{ close }">
        <div class="dropdown-menu">
          <router-link
            :to="{ name: 'Account' }"
            class="dropdown-item"
            @click="close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
            </svg>
            Account Settings
          </router-link>
          <button
            class="dropdown-item dropdown-item--danger"
            @click="logout"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.58L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z" fill="currentColor"/>
            </svg>
            Logout
          </button>
        </div>
      </template>
    </Popper>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import NotificationModal from "@/components/general/sidemodal/SideModal.vue";
import {
  truncate,
  abbreviate,
  capitalizeEachWord,
  formatAmountToDollar,
  removeUnderscore,
  formatToStringDate,
  formatISOTime,
} from "@/utils/helpers.js";
import EmptyState from "@/components/notification/EmptyState";
import { getItem } from "@/utils/storage";
import { computed } from "vue";
import Popper from "vue3-popper";

export default {
  name: "HeaderProfile",

  components: { EmptyState, NotificationModal, Popper },

  data() {
    return {
      loading: false,
      loadingMore: false,
      notifications: {},
      syndicateName: "",
      showNotification: false,
      activeTab: 'all',
      markingAllRead: false,
    };
  },
  provide() {
    return {
      showNotification: computed(() => this.showNotification),
    };
  },

  computed: {
    ...mapState({
      orgDetails: (state) => state.organisationModule.organisationDetails,
      notificationCount: (state) => state.organisationModule.notificationCount,
    }),
    filteredNotifications() {
      if (this.activeTab === 'unread') {
        const filtered = {};
        Object.keys(this.notifications).forEach(dateKey => {
          const unreadItems = this.notifications[dateKey].filter(n => !n.read);
          if (unreadItems.length > 0) {
            filtered[dateKey] = unreadItems;
          }
        });
        return filtered;
      }
      return this.notifications;
    },
  },

  created() {
    this.setDetails();

    try {
      const teamMemberData = getItem("team_member");

      if (teamMemberData) {
        this.teamMember = teamMemberData;
        if (!this.teamMember || typeof this.teamMember !== "object") {
          this.teamMember = null;
          console.log("Team member data is invalid or empty");
        }
      } else {
        this.teamMember = null;
      }
    } catch (e) {
      this.teamMember = null;
      console.error("Error processing team member data:", e);
    }
  },

  methods: {
    ...mapActions("organisationModule", [
      "setOrganisationProfile",
      "getNotifications",
      "getTotalUnreadNotifications",
      "markAllNotificationsRead",
    ]),
    ...mapActions(["logout", "showToast"]),

    capitalizeEachWord,
    formatAmountToDollar,
    removeUnderscore,
    truncate,
    abbreviate,
    formatToStringDate,
    formatISOTime,

    setDetails() {
      const details = getItem("org_details");
      this.setOrganisationProfile(details);
      this.getTotalUnreadNotifications();
    },

    openNotification() {
      this.page = 1;
      this.notifications = {};
      this.showNotification = true;
      this.loading = true;
      this.activeTab = 'all';
      this.fetchNotifications();
    },

    async fetchNotifications() {
      const response = await this.getNotifications({
        page: this.page,
        limit: this.limit,
      });
      if (response) {
        let dates = [];
        let group = {};
        const { page, pages, notifications } = response.data.data;
        notifications.map((notification, index) => {
          if (!dates.includes(notification.createdAt.split("T")[0])) {
            let date = notification.createdAt.split("T")[0];
            dates.push(notification.createdAt.split("T")[0]);
            let groupArray = notifications.filter(
              (noti) => date === noti.createdAt.split("T")[0]
            );
            group[date] = groupArray;
          }
        });
        this.notifications = { ...this.notifications, ...group };
        this.page = page;
        this.pages = pages;
        setTimeout(() => {
          this.setObserver();
        }, 100);
      }
      if (this.loading) this.loading = false;
      if (this.loadingMore) this.loadingMore = false;
      this.getTotalUnreadNotifications();
    },

    setObserver() {
      const options = {
        root: this.$refs.notification,
        rootMargin: "0px",
        threshold: 0.5,
      };
      const notificationBottom = this.$refs.notificationBottom;
      const observer = new IntersectionObserver(this.callback, options);
      observer.observe(notificationBottom);
    },

    callback(entries, observer) {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0 && this.page < this.pages) {
          this.loadingMore = true;
          this.page += 1;
          this.fetchNotifications();
        }
      });
    },

    handleNotificationClick(notification) {
      // Mark as read and handle specific notification types
      if (notification.type === 'Esop Invitation' || notification.type === 'Invitation') {
        // Navigate to invitation review
      } else if (notification.type === 'Gift') {
        // Navigate to gift details
      }
    },

    async markAllAsRead() {
      this.markingAllRead = true;
      try {
        const success = await this.markAllNotificationsRead();
        if (success) {
          // Update local state to mark all as read
          Object.keys(this.notifications).forEach(dateKey => {
            this.notifications[dateKey].forEach(notification => {
              notification.read = true;
            });
          });
          this.showToast({
            description: "All notifications marked as read",
            display: true,
            type: "success",
          });
        } else {
          this.showToast({
            description: "Failed to mark notifications as read",
            display: true,
            type: "error",
          });
        }
      } catch (error) {
        this.showToast({
          description: "Something went wrong",
          display: true,
          type: "error",
        });
      } finally {
        this.markingAllRead = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

.notification-wrapper {
  position: relative;
}

.notification-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
}

.notification-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #ef4444;
  color: #ffffff;
  font-size: 10px;
  font-weight: 500;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
}

.profile-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.profile-info {
  display: none;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;

  @media (min-width: 768px) {
    display: flex;
  }
}

.profile-name {
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  text-transform: capitalize;
}

.profile-role {
  font-size: 12px;
  color: #94a3b8;
}

.dropdown-icon {
  display: none;

  @media (min-width: 768px) {
    display: block;
  }
}

.dropdown-menu {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px;
  min-width: 200px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.1);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 400;
  color: #475569;
  text-decoration: none;
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  background: none;
  border: none;

  &:hover {
    background: #f8fafc;
    color: #0f172a;
  }

  &--danger {
    color: #ef4444;

    &:hover {
      background: rgba(239, 68, 68, 0.1);
      color: #ef4444;
    }
  }
}

// Notification modal scrollbar
.scrollbar::-webkit-scrollbar {
  display: none;
}

.scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.new-tag {
  background: rgba(77, 132, 255, 0.15);
  color: #4d84ff;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
}

// Notification Panel Redesign
.notification-panel {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #ffffff;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-bottom: 1px solid #f1f5f9;
    flex-shrink: 0;
  }

  &__tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 10px;
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 0 24px 24px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: #e2e8f0;
      border-radius: 3px;

      &:hover {
        background: #cbd5e1;
      }
    }
  }

  &__footer {
    padding: 16px 0;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
  }
}

.notification-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #475569;
  }

  &--active {
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background: #ef4444;
    color: #ffffff;
    font-size: 10px;
    font-weight: 500;
    border-radius: 9px;
  }
}

.mark-all-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: none;
  font-size: 13px;
  font-weight: 500;
  color: #4d84ff;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 6px;

  &:hover:not(:disabled) {
    background: rgba(77, 132, 255, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &--loading {
    color: #94a3b8;
  }

  &__loader {
    width: 14px;
    height: 14px;
    border: 2px solid #e2e8f0;
    border-top-color: #4d84ff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.notification-group {
  &__date {
    display: flex;
    align-items: center;
    padding: 20px 0 12px;

    span {
      font-size: 12px;
      font-weight: 500;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }
}

.notification-item {
  position: relative;
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #e2e8f0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  &--unread {
    background: linear-gradient(135deg, rgba(77, 132, 255, 0.02) 0%, rgba(77, 132, 255, 0.05) 100%);
    border-color: rgba(77, 132, 255, 0.2);

    &:hover {
      border-color: rgba(77, 132, 255, 0.3);
    }
  }

  &__indicator {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 24px;
    background: #4d84ff;
    border-radius: 0 3px 3px 0;
  }

  &__icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    overflow: hidden;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__icon-default {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
    color: #4d84ff;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  &__type {
    font-size: 13px;
    font-weight: 500;
    color: #0f172a;
  }

  &__time {
    font-size: 11px;
    color: #94a3b8;
  }

  &__message {
    font-size: 13px;
    color: #64748b;
    line-height: 1.5;
    margin: 0;
  }

  &__details {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    padding: 10px 12px;
    background: #f8fafc;
    border-radius: 8px;
  }

  &__detail {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #64748b;

    svg {
      color: #94a3b8;
    }
  }

  &__amount {
    font-size: 13px;
    font-weight: 500;
    color: #0f172a;
  }

  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    margin-top: 8px;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 500;
    border-radius: 6px;
    background: #f1f5f9;
    color: #64748b;

    &--accepted {
      background: rgba(34, 197, 94, 0.1);
      color: #16a34a;
    }

    &--terminated {
      background: rgba(239, 68, 68, 0.1);
      color: #ef4444;
    }
  }
}

.notification-action {
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &--primary {
    background: #4d84ff;
    color: #ffffff;

    &:hover {
      background: #3b6fe8;
    }
  }

  &--secondary {
    background: #f1f5f9;
    color: #475569;

    &:hover {
      background: #e2e8f0;
    }
  }
}

.notification-loader {
  position: relative;
  height: 60px;
}

.notification-end {
  text-align: center;
  font-size: 13px;
  color: #94a3b8;
  padding: 8px 0;
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    background: #f8fafc;
    border-radius: 50%;
    margin-bottom: 20px;
  }

  &__title {
    font-size: 16px;
    font-weight: 500;
    color: #0f172a;
    margin: 0 0 8px 0;
  }

  &__text {
    font-size: 13px;
    color: #94a3b8;
    margin: 0;
    max-width: 240px;
  }
}
</style>
